import { useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Ion } from "@/components/Ion";
import { getToken } from "@/lib/api";
import { Thumb, shrink } from "@/components/quote/PhotoScan";

const BASE = process.env.REACT_APP_BACKEND_URL;
const STEPS = [
  ["Tape a square", "on a typical spot of the real surface. Any size works, 4 x 4 in is easy."],
  ["Measure it", "inside the tape and type the width and height."],
  ["Start a stopwatch", "and laser-clean only inside the tape, at the settings you'll use on the job."],
  ["Stop when it's clean", "and enter the minutes and seconds. Snap a photo or short video as proof."],
];

export const PatchTip = ({ testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-2.5 rounded-[10px] border border-[#333] bg-[#121212]" data-testid={testId}>
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 p-2.5 text-left" data-testid={`${testId}-toggle`}>
        <Ion name="bulb-outline" size={15} color="#4CCC06" />
        <span className="flex-1 text-[12px] font-bold">How to time a test patch</span>
        <Ion name={open ? "chevron-up" : "chevron-down"} size={14} color="#A0A0A0" />
      </button>
      {open && (
        <div className="flex flex-col gap-3 px-2.5 pb-3 sm:flex-row" data-testid={`${testId}-body`}>
          <img src="/quote_art/patch_tip.jpg" alt="Taped 4 by 4 inch test patch cleaned to bare oak next to a stopwatch" className="w-full rounded-[8px] border border-[#333] object-cover sm:w-56" />
          <ol className="flex-1">
            {STEPS.map(([b, t], i) => (
              <li key={b} className="mb-1.5 flex gap-2 text-[12px] leading-4"><span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#4CCC06] text-[10px] font-extrabold text-black">{i + 1}</span><span><b>{b}</b> {t}</span></li>
            ))}
            <li className="mt-2 text-[11px] leading-4 text-[#A0A0A0]">Parts that clean differently, like molding or carved trim, can get their own patch on their row.</li>
          </ol>
        </div>
      )}
    </div>
  );
};

const upload = (file) => new Promise((resolve, reject) => {
  const fd = new FormData();
  fd.append("file", file, file.name || "proof.jpg");
  const x = new XMLHttpRequest();
  x.open("POST", `${BASE}/api/quote-builder/patch-proof`);
  x.setRequestHeader("Authorization", `Bearer ${getToken()}`);
  x.onload = () => { let d = {}; try { d = JSON.parse(x.responseText); } catch {} return x.status < 300 ? resolve(d) : reject(new Error(d.detail || "Upload failed")); };
  x.onerror = () => reject(new Error("Upload failed. Check your connection."));
  x.send(fd);
});

export const PatchProof = ({ proof, onChange, testId }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [vid, setVid] = useState(null);
  const openVideo = async () => {
    try {
      const r = await fetch(`${BASE}/api/quote-builder/photos/${proof.id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!r.ok) throw new Error();
      setVid(URL.createObjectURL(await r.blob()));
    } catch { toast.error("Couldn't open the video"); }
  };
  const closeVideo = () => { URL.revokeObjectURL(vid); setVid(null); };
  const pick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.type.startsWith("video/") && f.size > 150 * 1024 * 1024) return toast.error("Videos must be under 150 MB. Film a shorter clip.");
    setBusy(true);
    try {
      const file = f.type.startsWith("video/") ? f : new File([await shrink(f)], "proof.jpg", { type: "image/jpeg" });
      onChange(await upload(file)); toast.success("Patch proof saved with this quote");
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 flex items-center gap-2" data-testid={testId}>
      {proof ? <>
        {proof.kind === "video"
          ? <button onClick={openVideo} className="flex h-16 items-center gap-1.5 rounded-[8px] border border-[#4CCC06] px-3 text-[11px] font-extrabold text-[#4CCC06]" data-testid={`${testId}-play`}><Ion name="play-circle-outline" size={18} color="#4CCC06" />Patch video</button>
          : <span data-testid={`${testId}-photo`}><Thumb id={proof.id} /></span>}
        <button onClick={() => onChange(null)} className="text-[11px] font-bold text-[#EF4444]" data-testid={`${testId}-remove`}>Remove proof</button>
      </> : (
        <button onClick={() => ref.current?.click()} disabled={busy} className="flex items-center gap-1.5 rounded-full border border-dashed border-[#555] px-3 py-1.5 text-[11px] font-bold text-white hover:border-[#4CCC06] disabled:opacity-60" data-testid={`${testId}-add`}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Ion name="camera-outline" size={14} color="#fff" />}{busy ? "Uploading…" : "Add photo or video of the patch (optional)"}
        </button>
      )}
      <input ref={ref} type="file" accept="image/*,video/*" className="hidden" onChange={pick} data-testid={`${testId}-input`} />
      {vid && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4" onClick={closeVideo} data-testid={`${testId}-video-modal`}>
          <video src={vid} controls autoPlay playsInline className="max-h-[80vh] max-w-full rounded-[10px]" onClick={(e) => e.stopPropagation()} />
          <button onClick={closeVideo} className="absolute right-4 top-4 rounded-full bg-white/10 p-2" data-testid={`${testId}-video-close`} aria-label="Close"><Ion name="close" size={20} color="#fff" /></button>
        </div>
      )}
    </div>
  );
};
